/**
 * Base para Web Components em Light DOM (sem Shadow DOM).
 * Uso: class MinhaSecao extends EssenceBaseComponent { init() { ... } }
 * Opcional: destroy() — limpar Swiper/timers/listeners em window ou document sem signal; deve ser idempotente.
 * Chamado antes de re-init no editor (shopify:section:load) e no disconnectedCallback (secção removida).
 * teamdropInitProductCards: inicialização global de .product-card (depois de theme.init no load).
 * theme.form — selects estilizados (.custom-select); depende de theme (global.js), mesmo bundle.
 * Carregar no layout: <script src="{{ 'base.js' | asset_url }}" defer></script>
 */

class EssenceBaseComponent extends HTMLElement {
  /**
   * Override em subclasses para ajustar lazy init:
   *   get eagerInit() { return true; }                   // dispara init imediato (sem observer)
   *   get observerRootMargin() { return "..."; }         // padrão: dispara quando o elemento entra 15% do viewport
   *   get observerThreshold() { return 0; }              // qual fração do alvo precisa estar visível
   */
  get eagerInit() { return false; }
  get observerRootMargin() { return "0px 0px -15% 0px"; }
  get observerThreshold() { return 0; }

  connectedCallback() {
    if (this.__tdConnected) return;
    this.__tdConnected = true;

    this.__tdController = new AbortController();
    const signal = this.__tdController.signal;

    // Listeners do Shopify Editor: só registrar em design_mode (não disparam fora do editor).
    // OBS: shopify:section:load NÃO é tratado aqui de propósito. Quando a section é
    // recarregada pelo editor, o Shopify substitui toda a innerHTML, então os elementos
    // antigos disparam disconnectedCallback (AbortController limpa listeners) e os novos
    // disparam connectedCallback (init fresh). Adicionar re-init manual aqui causa
    // double-bind nos componentes recém-criados.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener(
        "shopify:block:select",
        (event) => {
          if (event.target && this.contains(event.target)) {
            this.onEditorBlockSelect?.(event);
          }
        },
        { signal }
      );

      document.addEventListener(
        "shopify:block:deselect",
        (event) => {
          if (event.target && this.contains(event.target)) {
            this.onEditorBlockDeselect?.(event);
          }
        },
        { signal }
      );

      document.addEventListener(
        "shopify:section:unload",
        (event) => {
          if (event.target && event.target.contains(this)) {
            this.onEditorSectionUnload?.(event);
          }
        },
        { signal }
      );
    }

    var isEager = this.hasAttribute("data-eager") || this.eagerInit === true;
    if (isEager || !("IntersectionObserver" in window)) {
      this.initOnce();
    } else {
      this.__tdObserver = new IntersectionObserver(
        (entries) => {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              this.initOnce();
              if (this.__tdObserver) {
                this.__tdObserver.disconnect();
                this.__tdObserver = null;
              }
              break;
            }
          }
        },
        {
          rootMargin: this.observerRootMargin,
          threshold: this.observerThreshold,
        }
      );
      this.__tdObserver.observe(this);
    }
  }

  initOnce() {
    if (this.__tdInitialized) return;
    this.__tdInitialized = true;
    this.init();
  }

  init() {}

  disconnectedCallback() {
    this.destroy?.();
    if (this.__tdObserver) {
      this.__tdObserver.disconnect();
      this.__tdObserver = null;
    }
    if (this.__tdController) {
      this.__tdController.abort();
      this.__tdController = null;
    }
    this.__tdConnected = false;
    this.__tdInitialized = false;
  }
}

window.EssenceBaseComponent = EssenceBaseComponent;

/**
 * <modal-element name="X"> — sistema central de modal do tema.
 * Substitui o antigo theme.component.modal (era em global.js).
 *
 * Markup:
 *   <modal-element name="popup" class="modal hide opacity-0">...</modal-element>
 *
 * Triggers (qualquer lugar do DOM):
 *   <button data-modal-open="popup">Abrir</button>
 *   <button data-modal-close="popup">Fechar</button>  // dentro ou fora do modal
 *
 * Fecha também ao clicar no overlay (fora de .modal-content).
 *
 * Eventos custom no <modal-element>:
 *   - "modal:open"  (bubbles=true)
 *   - "modal:close" (bubbles=true)  — dispara DEPOIS da animação fade-out terminar
 *
 * API programática (em JS):
 *   var modal = document.querySelector('modal-element[name="popup"]');
 *   modal.open();  modal.close();
 */
class ModalElement extends EssenceBaseComponent {
  get eagerInit() { return true; }

  init() {
    this.addEventListener("click", this.__onModalClick.bind(this), {
      signal: this.__tdController.signal,
    });
  }

  __onModalClick(e) {
    if (e.target.closest("[data-modal-close]")) {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.target === this) {
      this.close();
    }
  }

  open() {
    this.classList.remove("hide", "fade-out");
    requestAnimationFrame(() => {
      this.classList.add("show");
    });
    this.setAttribute("aria-hidden", "false");
    document.body.classList.add("noscroll");
    this.dispatchEvent(new CustomEvent("modal:open", { bubbles: true }));
  }

  close() {
    this.classList.add("fade-out");
    this.classList.remove("show");
    var self = this;
    window.setTimeout(function () {
      self.classList.add("hide");
      self.setAttribute("aria-hidden", "true");
      document.body.classList.remove("noscroll");
      self.dispatchEvent(new CustomEvent("modal:close", { bubbles: true }));

      // Modais injetados dinamicamente (quickview): remover do DOM após fechar.
      if (self.hasAttribute("data-modal-dynamic")) {
        window.setTimeout(function () {
          if (self.parentNode) self.parentNode.removeChild(self);
        }, 100);
      }
    }, 300);
  }
}

if (window.customElements && !customElements.get("modal-element")) {
  customElements.define("modal-element", ModalElement);
}

window.ModalElement = ModalElement;

// Delegação global para [data-modal-open="X"] (um único listener no document).
if (!window.__teamdropModalOpenBound) {
  window.__teamdropModalOpenBound = true;
  document.addEventListener("click", function (e) {
    var opener = e.target.closest("[data-modal-open]");
    if (!opener) return;
    e.preventDefault();
    var name = opener.getAttribute("data-modal-open");
    var modal = document.querySelector('modal-element[name="' + name + '"]');
    if (modal && typeof modal.open === "function") modal.open();
  });
}

/**
 * <accordion-element> — accordion expansível por clique no header.
 * Substitui theme.component.accordion (era em global.js).
 *
 * Markup:
 *   <accordion-element class="accordion" [open]>
 *     <div class="accordion-header">Título</div>
 *     <div class="accordion-panel"><div>Conteúdo...</div></div>
 *   </accordion-element>
 *
 * Estado inicial via attribute `open` (mesma semântica do <details>).
 * Sem `open` → painel inicia fechado (style.height=0).
 * Com `open` → painel inicia visível (height auto).
 *
 * Eventos: "accordion:open", "accordion:close" (bubbles).
 */
class AccordionElement extends EssenceBaseComponent {
  get eagerInit() { return true; }

  get open() { return this.hasAttribute("open"); }
  set open(value) {
    if (value) this.setAttribute("open", "");
    else this.removeAttribute("open");
  }

  init() {
    this.accordionPanel = this.querySelector(".accordion-panel");
    if (!this.accordionPanel) return;

    var signal = this.__tdController.signal;
    this.addEventListener("click", this.__onClick.bind(this), { signal: signal });
    this.addEventListener("transitionend", this.__onTransitionEnd.bind(this), { signal: signal });
    this.addEventListener("contentClose", this.__onContentClose.bind(this), { signal: signal });

    // Estado inicial
    if (this.open) {
      this.classList.add("is-open");
    } else {
      this.accordionPanel.style.height = "0";
    }
  }

  __onClick(e) {
    if (!e.target.closest(".accordion-header")) return;
    e.preventDefault();
    this.toggle();
  }

  __onContentClose() {
    this.toggle();
  }

  toggle() {
    if (this.open) this.close(); else this.openPanel();
  }

  openPanel() {
    this.accordionPanel.style.height = "0";
    this.open = true;
    this.__setPanelHeight();
    var self = this;
    window.setTimeout(function () {
      self.classList.add("is-open");
    }, 10);
    this.dispatchEvent(new CustomEvent("accordion:open", { bubbles: true }));
  }

  close() {
    this.__setPanelHeight();
    this.classList.remove("is-open");
    this.classList.add("is-closing");
    var self = this;
    window.setTimeout(function () {
      self.accordionPanel.style.height = "0";
    }, 10);
    this.dispatchEvent(new CustomEvent("accordion:close", { bubbles: true }));
  }

  __setPanelHeight() {
    this.accordionPanel.style.height = this.accordionPanel.scrollHeight + "px";
  }

  __onTransitionEnd(e) {
    if (!e.target.matches(".accordion-panel")) return;
    if (this.classList.contains("is-closing")) {
      // Fechou: limpa is-closing e remove attribute open
      this.classList.remove("is-closing");
      this.open = false;
      // mantém height 0 inline (estado fechado)
    } else if (this.open) {
      // Abriu: remove style.height para voltar a height auto (responsivo a mudanças de conteúdo)
      this.accordionPanel.removeAttribute("style");
    }
  }
}

if (window.customElements && !customElements.get("accordion-element")) {
  customElements.define("accordion-element", AccordionElement);
}
window.AccordionElement = AccordionElement;

/**
 * <tab-element> — sistema de abas com nav + paineis.
 * Substitui theme.component.tab (era em global.js).
 *
 * Markup:
 *   <tab-element class="ab-tab">
 *     <div class="ab-tab__nav" data-tab-panel-id="painelA">Tab A</div>
 *     <div class="ab-tab__nav" data-tab-panel-id="painelB">Tab B</div>
 *     <div class="ab-tab__panel" id="painelA">...</div>
 *     <div class="ab-tab__panel" id="painelB">...</div>
 *   </tab-element>
 */
class TabElement extends EssenceBaseComponent {
  get eagerInit() { return true; }

  init() {
    this.activeClass = "ab-tab__nav--active";
    this.panelActiveClass = "ab-tab__panel--active";

    var navs = this.querySelectorAll(".ab-tab__nav");
    var panels = this.querySelectorAll(".ab-tab__panel");
    if (navs.length === 0) return;

    if (!this.querySelector("." + this.activeClass)) {
      navs[0].classList.add(this.activeClass);
    }
    if (panels.length > 0 && !this.querySelector("." + this.panelActiveClass)) {
      panels[0].classList.add(this.panelActiveClass);
    }

    var self = this;
    var signal = this.__tdController.signal;
    navs.forEach(function (nav) {
      nav.addEventListener("click", self.__onNavClick.bind(self), { signal: signal });
    });
  }

  __onNavClick(e) {
    e.preventDefault();
    var nav = e.target.closest(".ab-tab__nav");
    if (!nav) return;
    var panelId = nav.dataset.tabPanelId;

    var activeNav = this.querySelector("." + this.activeClass);
    if (activeNav) activeNav.classList.remove(this.activeClass);
    var activePanel = this.querySelector("." + this.panelActiveClass);
    if (activePanel) activePanel.classList.remove(this.panelActiveClass);

    nav.classList.add(this.activeClass);
    var targetPanel = this.querySelector("#" + panelId);
    if (targetPanel) targetPanel.classList.add(this.panelActiveClass);
  }
}

if (window.customElements && !customElements.get("tab-element")) {
  customElements.define("tab-element", TabElement);
}
window.TabElement = TabElement;

/**
 * Tooltip — sistema centralizado, sem Web Component.
 * Tooltips são marcadores em elementos pré-existentes ([data-tooltip-text]),
 * então fica como helper auto-bind por scan + delegação de hover.
 *
 * Substitui theme.component.tooltip + theme.component.renderTooltip.
 */
function teamdropInitTooltips(root) {
  if (window.innerWidth < 1024) return;
  if (!root || !root.querySelectorAll) return;
  root
    .querySelectorAll("[data-tooltip-text]:not([data-tooltip-rendered])")
    .forEach(teamdropBindTooltip);
}

function teamdropBindTooltip(toggle) {
  var TOOLTIP_ARROW_SIZE = 10;

  function createTip() {
    var tip = document.createElement("div");
    tip.classList.add("tooltip", "tooltip--" + (toggle.dataset.tooltipPosition || "bottom"));
    tip.setAttribute("role", "tooltip");
    tip.innerHTML = '<div class="tooltip-inner">' + toggle.dataset.tooltipText + "</div>";
    return tip;
  }

  function setPosition(tip) {
    var w = tip.clientWidth;
    var h = tip.clientHeight;
    var rect = toggle.getBoundingClientRect();
    var top, left;
    var position = toggle.dataset.tooltipPosition;
    switch (position) {
      case "top":
        top = rect.y;
        left = rect.x + (rect.width - w) / 2;
        tip.style.transform = "translateY(calc((100% + " + (TOOLTIP_ARROW_SIZE + 20) + "px) * -1))";
        break;
      case "left":
        top = rect.y + (rect.height - h) / 2;
        left = rect.x;
        tip.style.transform = "translateX(calc((100% + " + (TOOLTIP_ARROW_SIZE + 20) + "px) * -1))";
        break;
      case "right":
        top = rect.y + (rect.height - h) / 2;
        left = rect.x + rect.width + TOOLTIP_ARROW_SIZE;
        tip.style.transform = "translateX(20px)";
        break;
      default:
        top = rect.y + rect.height + TOOLTIP_ARROW_SIZE;
        left = rect.x + (rect.width - w) / 2;
        tip.style.transform = "translateY(20px)";
    }
    tip.style.translate = left + "px " + top + "px";
  }

  function animateIn(tip) {
    tip.style.transition = "transform 0.3s ease-in-out, opacity 0.3s ease-in-out";
    var position = toggle.dataset.tooltipPosition;
    switch (position) {
      case "top":
        tip.style.transform = "translateY(calc((100% + " + TOOLTIP_ARROW_SIZE + "px) * -1))";
        break;
      case "left":
        tip.style.transform = "translateX(calc((100% + " + TOOLTIP_ARROW_SIZE + "px) * -1))";
        break;
      case "right":
        tip.style.transform = "translateX(0%)";
        break;
      default:
        tip.style.transform = "translateY(0%)";
    }
  }

  var currentTip = null;

  function onEnter() {
    currentTip = createTip();
    document.body.append(currentTip);
    setPosition(currentTip);
    window.setTimeout(function () {
      if (!currentTip) return;
      currentTip.classList.remove("hide");
      currentTip.classList.add("show");
      animateIn(currentTip);
    }, 10);
  }

  function onLeave() {
    if (!currentTip) return;
    currentTip.classList.remove("show");
    currentTip.classList.add("hide");
    var tip = currentTip;
    currentTip = null;
    window.setTimeout(function () {
      if (tip.parentNode) tip.parentNode.removeChild(tip);
    }, 300);
  }

  toggle.addEventListener("mouseenter", onEnter);
  toggle.addEventListener("mouseleave", onLeave);
  toggle.addEventListener("click", onLeave);
  toggle.setAttribute("data-tooltip-rendered", "");
}

window.teamdropInitTooltips = teamdropInitTooltips;

/**
 * Hub global de scroll (compatível com o contrato legado de callbacks + addEvent).
 * Fica disponível para migração incremental sem acoplar ao ciclo de vida de um componente.
 */
function createTeamDropOnScrollHub() {
  var callbacks = [];
  var started = false;
  var dirty = false;
  var intervalId = null;

  function onScroll() {
    dirty = true;
  }

  function addEvent() {
    if (started) return;
    started = true;

    window.addEventListener("scroll", onScroll, { passive: true });

    intervalId = window.setInterval(function () {
      if (!dirty) return;
      dirty = false;

      callbacks.slice().forEach(function (cb) {
        if (typeof cb === "function") cb();
      });
    }, 10);
  }

  function destroy() {
    if (!started) return;
    started = false;
    window.removeEventListener("scroll", onScroll);
    if (intervalId) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  }

  return {
    callbacks: callbacks,
    addEvent: addEvent,
    destroy: destroy, // deprecado, usar disconnectedCallback
    get started() {
      return started;
    },
  };
}

if (!window.teamdropOnScrollHub) {
  window.teamdropOnScrollHub = createTeamDropOnScrollHub();
}

/*
 * theme.utilities — helpers transversais (animação, lazy srcset, debounce,
 * spinner, focus trap, fetch helpers). Antes ficava em global.js; movido
 * pra cá porque é infra usada pelos Web Components e por todos os módulos
 * carregados depois (search, collection, product, cart, wishlist, etc.).
 *
 * Load order (defer): global.js → base.js → vendor.js → swiper-slider.js
 * → ... módulos. base.js sempre roda antes de qualquer caller, e o bootstrap
 * em global.js (DOMContentLoaded) também executa depois.
 */
theme.utilities = {
  animationObserver: new IntersectionObserver((e, t) => {
    e.forEach((e, i) => {
      e.isIntersecting &&
        setTimeout(function () {
          e.target.classList.add("animated");
        }, 100),
        e.target.addEventListener("animationend", function (e) {
          e.target.classList.contains("animated") &&
            setTimeout(function () {
              e.target.classList.remove("slideUp"),
                e.target.classList.remove("fadeIn"),
                e.target.classList.remove("animated"),
                t.unobserve(e.target);
            }, 1e3);
        });
    });
  }),
  applyAnimation: function () {
    document
      .querySelectorAll(".slideUp:not(.animated), .fadeIn:not(.animated)")
      .forEach((e) => {
        "IntersectionObserver" in window
          ? this.animationObserver.observe(e)
          : (e.classList.add("animated"),
            e.classList.remove("slideUp"),
            e.classList.remove("fadeIn"));
      });
  },
  applyImagesSrcset: function () {
    var e;
    0 < document.getElementsByClassName("pr-image").length &&
      ((e = document.getElementsByClassName("pr-image")),
      this.imagesSrcset(e));
  },
  imagesSrcset: function (e) {
    Array.from(e).forEach(function (e) {
      var t, i;
      void 0 !== e.dataset.widths &&
        ((t = e.dataset.widths.replace(/\[|\]/g, "").split(", ")),
        (i = ""),
        t.forEach(function (t) {
          var n = e.dataset.src.replace(/{width}/, t);
          i += "".concat(n).concat(" ", t).concat("w, ");
        }),
        (e.dataset.srcset = i),
        e.classList.add("lazyload"));
    });
  },
  debounce: function (e, t = 300) {
    let i;
    return (...n) => {
      clearTimeout(i), (i = setTimeout(() => e.apply(this, n), t));
    };
  },
  showAlert: function (e, t, i, n) {
    let r = document.createElement("div");
    (r.innerHTML = i),
      r.classList.add("notif", "notif-" + e),
      t.insertBefore(r, t.firstChild),
      setTimeout(function () {
        r.classList.add("opacity-0");
      }, n),
      r.addEventListener("transitionend", function () {
        r.remove();
      });
  },
  getSectionUrl: function (e) {
    var t = document.getElementById("product-grid").dataset.id;
    return window.location.pathname + `?section_id=${t}&` + e;
  },
  fetchConfig: function (e = "json") {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/" + e,
      },
    };
  },
  getFocusableElements: function (e) {
    return Array.from(
      e.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    );
  },
  trapFocus: function (e, t = !1) {
    let i = this.getFocusableElements(e),
      n = i[0];
    var r = i[i.length - 1];
    let o = null;
    n.focus(),
      (o = n),
      document.addEventListener(
        "focus",
        (e) => {
          e.preventDefault(),
            (o = i.includes(e.target)
              ? e.target
              : (o === n && n.focus(),
                t || (o === r && n.focus()),
                document.activeElement));
        },
        !0
      ),
      document.addEventListener(
        "keydown",
        (e) => {
          e.shiftKey && t && (console.log("shiftkey"), (o = null));
        },
        !0
      );
  },
  onFocus: function () {
    let e = document.querySelectorAll(".menu-lvl-1");
    0 < e.length &&
      e.forEach(function (e) {
        e.addEventListener("focus", function (e) {
          let t = e.currentTarget.querySelector(".main-navigation__dropdown");
          t.setAttribute("tabindex", "-1"),
            t && (console.log("container: " + t.classList), trapFocus(t, !0));
        });
      });
  },
  getListFromStorage: function (e) {
    return (e = window.localStorage.getItem(e)) && 0 < e.length
      ? JSON.parse(e)
      : [];
  },
  toggleSpinner: function (el, on) {
    if (!el) return;
    el.classList.toggle("with-loader--show", !!on);
    var spinner = el.querySelector(".spinner");
    if (spinner) spinner.classList.toggle("animating", !!on);
  },
  fetchHtmlFragment: function (url, selector) {
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error("Fetch failed: " + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        return selector ? doc.querySelector(selector) : doc;
      });
  },
};

// Infra de notificacao global reutilizavel em todo o tema.
theme.notification = {};
theme.notification.component = {
  duration: 5000,
  type: null,
  title: null,
  text: null,
  show: function () {
    if (!this.type) return;

    this.item = document.createElement("div");
    this.item.classList.add(
      "notification",
      "notification--" + this.type,
      "mb-1",
      "transition-transform",
      "translate-x-hidden"
    );

    var content = document.createElement("div");
    content.classList.add("notification-content");

    if (this.title) {
      var titleEl = document.createElement("div");
      titleEl.classList.add("h6");
      titleEl.innerHTML = this.title;
      content.appendChild(titleEl);
    }

    if (this.text) {
      var textEl = document.createElement("div");
      textEl.innerHTML = this.text;
      content.appendChild(textEl);
    }

    var buttonContainer = document.createElement("div");
    buttonContainer.classList.add("notification-btn");
    var button = document.createElement("button");
    button.innerHTML = window.icons.cross;
    button.addEventListener("click", this.handleBtnClose.bind(this));
    buttonContainer.appendChild(button);
    content.appendChild(buttonContainer);
    this.item.appendChild(content);

    var container = document.querySelector(".notification-container");
    if (!container) {
      container = document.createElement("div");
      container.classList.add("notification-container");
      document.querySelector("body").append(container);
    }
    container.append(this.item);

    setTimeout(() => {
      this.item.classList.remove("translate-x-hidden");
      this.item.classList.add("translate-0");
    }, 300);

    this.hideByDurationId = setTimeout(() => {
      this.hide();
    }, this.duration);
  },
  handleBtnClose: function (event) {
    event.preventDefault();
    clearTimeout(this.hideByDurationId);
    this.hide();
  },
  hide: function () {
    this.item.classList.add("translate-x-hidden");
    this.item.classList.remove("translate-0");
    setTimeout(() => {
      this.item.remove();
    }, 1000);
  },
};

theme.notification.error = function (title, text) {
  var notification = Object.create(theme.notification.component);
  notification.type = "error";
  notification.title = title;
  notification.text = text;
  notification.show();
};

theme.notification.success = function (title, text) {
  var notification = Object.create(theme.notification.component);
  notification.type = "success";
  notification.title = title;
  notification.text = text;
  notification.show();
};

theme.notification.info = function (title, text) {
  var notification = Object.create(theme.notification.component);
  notification.type = "info";
  notification.title = title;
  notification.text = text;
  notification.show();
};

theme.notification.warning = function (title, text) {
  var notification = Object.create(theme.notification.component);
  notification.type = "warning";
  notification.title = title;
  notification.text = text;
  notification.show();
};

theme.form = {
  render: function () {
    var e = Object.create(theme.form.customSelect);
      (e.selectEl = document.querySelectorAll(
        ".custom-select:not(.custom-select--rendered)"
      )),
        e.render(),
        e.setListener();
    },
    customSelect: {
      selectEl: null,
      render: function () {
        if (
          ((this.selectEl =
            this.selectEl || document.querySelectorAll(".custom-select")),
            0 !== this.selectEl.length)
        ) {
          var e, t, i, n, r, o, s;
          this.selectEl.forEach(function (a) {
            (e = a.querySelector("select")),
              (i = document.createElement("div")).classList.add(
                "options-container",
                "hide"
              ),
              a.appendChild(i),
              (n =
                (n = e.querySelector("option[selected]")) ||
                e.querySelector("option")),
              (o = document.createElement("span")).classList.add(
                "selected-value"
              ),
              (o.innerHTML = n.innerHTML),
              (s = document.createElement("span")).classList.add(
                "select-arrow"
              ),
              (s.innerHTML =
                '<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-down"><polyline points="6 9 12 15 18 9"></polyline></svg>'),
              (r = document.createElement("div")),
              a.appendChild(r),
              r.classList.add("select-bar"),
              r.appendChild(o),
              r.appendChild(s),
              Array.from(e.options).forEach(function (e, n) {
                (t = document.createElement("div")).classList.add(
                  "option-item"
                ),
                  e.getAttribute("selected") &&
                  t.classList.add("item-selected"),
                  (t.innerHTML = e.innerHTML),
                  (t.dataset.index = n),
                  i.appendChild(t);
              }),
              i.getBoundingClientRect().x + i.offsetWidth > window.innerWidth &&
              i.classList.add("right-0"),
              a.classList.add("custom-select--rendered");
          });
        }
      },
      setListener: function () {
        (this.handleDocumentClick = this.handleClickEvent.bind(this)),
          document.addEventListener("click", this.handleDocumentClick);
      },
      handleOptionClick: function (e) {
        var t = e.closest(".custom-select").querySelector("select");
        (t.value = t.options[e.dataset.index].value),
          t.dispatchEvent(new Event("change", { bubbles: !0 })),
          (e
            .closest(".custom-select")
            .querySelector(".select-bar span").innerHTML = e.innerHTML),
          this.closeDropdown(e.closest(".custom-select"));
      },
      toggleOpen: function (e) {
        e.classList.toggle("open"),
          e.querySelector(".options-container").classList.toggle("hide"),
          e.querySelector(".select-bar").classList.toggle("active");
        var t = this;
        e.classList.contains("open") &&
          document.querySelectorAll(".custom-select.open").forEach(function (i) {
            i !== e && t.closeDropdown(i);
          });
      },
      closeDropdown: function (e) {
        e.classList.remove("open"),
          e.querySelector(".options-container").classList.add("hide"),
          e.querySelector(".select-bar").classList.remove("active");
      },
      handleClickEvent: function (e) {
        var t = this;
        e.target.closest(".custom-select")
          ? e.target.classList.contains("option-item")
            ? this.handleOptionClick(e.target)
            : ((e = e.target.closest(".custom-select")), this.toggleOpen(e))
          : document.querySelectorAll(".custom-select.open").forEach(function (e) {
            t.closeDropdown(e);
          });
      },
    },
  };

/**
 * Inicializa cartões de produto (quick view, wishlist, etc.) em qualquer raiz do DOM.
 * Faz setupCardRoot + renderTooltip num só passo. Idempotente via [card-rendered].
 * Depende de ProductCardElement.setupCardRoot (assets/product.js).
 */
function teamdropInitProductCards(root) {
  if (!root || typeof ProductCardElement === "undefined" || !ProductCardElement.setupCardRoot) return;
  root.querySelectorAll(".product-card:not([card-rendered])").forEach(function (el) {
    ProductCardElement.setupCardRoot(el);
  });
  teamdropInitTooltips(root);
}

/**
 * <link-copier> — botão que copia link para o clipboard.
 *
 * Markup:
 *   <link-copier class="link-copier no-js-hidden">
 *     <input type="hidden" value="{{ url }}">
 *     <button class="link-copier__btn">Copiar</button>
 *     <span class="link-copier__success d-none">Copiado!</span>
 *   </link-copier>
 */
class LinkCopierElement extends EssenceBaseComponent {
  get eagerInit() { return true; }

  init() {
    var btn = this.querySelector(".link-copier__btn");
    if (!btn) return;
    btn.addEventListener("click", this.__onClick.bind(this), {
      signal: this.__tdController.signal,
    });
  }

  __onClick() {
    var input = this.querySelector("input");
    var url = (input && input.value) || document.location.href;
    var success = this.querySelector(".link-copier__success");
    var self = this;
    navigator.clipboard.writeText(url).then(function () {
      if (!success) return;
      success.classList.remove("d-none");
      success.textContent = window.accessibilityStrings && window.accessibilityStrings.shareSuccess;
      window.setTimeout(function () {
        success.classList.add("d-none");
      }, 5000);
    });
  }
}

if (window.customElements && !customElements.get("link-copier")) {
  customElements.define("link-copier", LinkCopierElement);
}

/**
 * <localization-form> — submita o form pai quando um <select> filho muda.
 * Substitui theme.component.localization (era em global.js).
 *
 * Markup:
 *   <localization-form>
 *     <select class="localization-selector link" name="locale_code">...</select>
 *   </localization-form>
 */
class LocalizationFormElement extends EssenceBaseComponent {
  get eagerInit() { return true; }

  init() {
    var select = this.querySelector("select");
    if (!select) return;
    select.addEventListener("change", this.__onChange.bind(this), {
      signal: this.__tdController.signal,
    });
  }

  __onChange(e) {
    var form = e.currentTarget.closest("form");
    if (form) form.submit();
  }
}

if (window.customElements && !customElements.get("localization-form")) {
  customElements.define("localization-form", LocalizationFormElement);
}

// Auto-init: script defer roda após DOM parseado. Sem window.load.
// <product-card> é Web Component lazy — não precisa de init global aqui.
function teamdropBootstrapBase() {
  teamdropInitTooltips(document);
  if (theme.form && typeof theme.form.render === "function") {
    theme.form.render();
  }
}

if (document.readyState === "complete") {
  teamdropBootstrapBase();
} else {
  document.addEventListener("DOMContentLoaded", teamdropBootstrapBase);
}

document.addEventListener("shopify:section:load", function (event) {
  if (!event.target) return;
  teamdropInitProductCards(event.target);
  teamdropInitTooltips(event.target);
});

window.teamdropInitProductCards = teamdropInitProductCards;
